/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Slide,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import parse from 'date-fns/parse';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useIntl } from 'react-intl';
import { Download, ShareButton } from 'components/Download';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TITLE = {
  V_HAROMINC: 'Voltage Harmonic',
  I_HARMONIC: 'Current Harmonic',
  V_INTERHARMONIC: 'Voltage InterHarmonic',
  I_INTERHARMONIC: 'Current InterHarmonic',
};

const TITLES = {
  V_HAROMINC: 'Voltaje Armónico',
  I_HARMONIC: 'Corriente Armónica',
  V_INTERHARMONIC: 'Voltaje InterArmónico',
  I_INTERHARMONIC: 'Corriente InterArmónico',
};

export default function HarmonicChart({ title, data, color, icon, footer }) {
  const [isOpen, setIsOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [dataChart, setDataChart] = useState([]);
  const [selectData, setSelectData] = useState([1, 3, 5, 7, 9].sort());
  const intl = useIntl();
  const chartRef = useRef(null);

  const colors = [
    '#10b981',
    '#3b82f6',
    '#f59e0b',
    '#730065',
    '#ef4444',
    '#8b5cf6',
    '#ec4899',
    '#22d3ee',
    '#84cc16',
    '#eab308',
    '#6366f1',
    '#14b8a6',
    '#f97316',
    '#db2777',
    '#4ade80',
    '#7c3aed',
    '#0ea5e9',
    '#a855f7',
    '#facc15',
    '#6ee7b7',
    '#fb923c',
    '#3f6212',
    '#c026d3',
    '#2dd4bf',
    '#fde68a',
    '#f87171',
    '#60a5fa',
    '#d946ef',
    '#34d399',
    '#a3e635',
    '#fcd34d',
  ];

  const parsed = (date) => parse(date, 'yyyyMMddHHmm', new Date());

  useEffect(() => {
    if (!data?.length) {
      setDataChart([]);
      return;
    }

    let key = '';
    switch (title) {
      case TITLE.V_HAROMINC:
        key = 'v_Harmonics_';
        break;
      case TITLE.I_HARMONIC:
        key = 'i_Harmonics_';
        break;
      case TITLE.V_INTERHARMONIC:
        key = 'v_InterHarmonics_';
        break;
      case TITLE.I_INTERHARMONIC:
        key = 'i_InterHarmonics_';
        break;
      default:
        break;
    }

    const formatted = data
      .map((el) => ({
        date: parsed(el.timestamp),
        l1: selectData.map((i) => el[`${key}L1`][i]),
        l2: selectData.map((i) => el[`${key}L2`][i]),
        l3: selectData.map((i) => el[`${key}L3`][i]),
      }))
      .sort((a, b) => a.date - b.date);

    setDataChart(formatted);
  }, [title, data, selectData]);

  Highcharts.setOptions({
    lang: {
      months: [
        intl.formatMessage({ id: 'app.months.jan' }),
        intl.formatMessage({ id: 'app.months.feb' }),
        intl.formatMessage({ id: 'app.months.mar' }),
        intl.formatMessage({ id: 'app.months.apr' }),
        intl.formatMessage({ id: 'app.months.may' }),
        intl.formatMessage({ id: 'app.months.jun' }),
        intl.formatMessage({ id: 'app.months.jul' }),
        intl.formatMessage({ id: 'app.months.ago' }),
        intl.formatMessage({ id: 'app.months.sep' }),
        intl.formatMessage({ id: 'app.months.oct' }),
        intl.formatMessage({ id: 'app.months.nov' }),
        intl.formatMessage({ id: 'app.months.dec' }),
      ],
      weekdays: [
        intl.formatMessage({ id: 'app.daysOfWeek.sun' }),
        intl.formatMessage({ id: 'app.daysOfWeek.mon' }),
        intl.formatMessage({ id: 'app.daysOfWeek.tue' }),
        intl.formatMessage({ id: 'app.daysOfWeek.wed' }),
        intl.formatMessage({ id: 'app.daysOfWeek.thu' }),
        intl.formatMessage({ id: 'app.daysOfWeek.fri' }),
        intl.formatMessage({ id: 'app.daysOfWeek.sat' }),
      ],
      shortMonths: [
        intl.formatMessage({ id: 'app.monthsShort.jan' }),
        intl.formatMessage({ id: 'app.monthsShort.feb' }),
        intl.formatMessage({ id: 'app.monthsShort.mar' }),
        intl.formatMessage({ id: 'app.monthsShort.apr' }),
        intl.formatMessage({ id: 'app.monthsShort.may' }),
        intl.formatMessage({ id: 'app.monthsShort.jun' }),
        intl.formatMessage({ id: 'app.monthsShort.jul' }),
        intl.formatMessage({ id: 'app.monthsShort.aug' }),
        intl.formatMessage({ id: 'app.monthsShort.sep' }),
        intl.formatMessage({ id: 'app.monthsShort.oct' }),
        intl.formatMessage({ id: 'app.monthsShort.nov' }),
        intl.formatMessage({ id: 'app.monthsShort.dec' }),
      ],
      shortWeekdays: [
        intl.formatMessage({ id: 'app.daysOfWeekShort.sun' }),
        intl.formatMessage({ id: 'app.daysOfWeekShort.mon' }),
        intl.formatMessage({ id: 'app.daysOfWeekShort.tue' }),
        intl.formatMessage({ id: 'app.daysOfWeekShort.wed' }),
        intl.formatMessage({ id: 'app.daysOfWeekShort.thu' }),
        intl.formatMessage({ id: 'app.daysOfWeekShort.fri' }),
        intl.formatMessage({ id: 'app.daysOfWeekShort.sat' }),
      ],
    },
    turboThreshold: 0,
    time: {
      useUTC: false,
      timezone: 'America/Mexico_City',
    },
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      type: 'datetime',
      crosshair: true,

      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M',
        hour: '%H:%M',
        day: '%a %e. %b',
        week: '%e. %b',
        month: "%b '%y",
        year: '%Y',
      },
    },
    rangeSelector: {
      enabled: true,
      selected: 4,
    },
    tooltip: {
      shared: true,
      positioner: function (labelWidth, labelHeight, point) {
        const chart = this.chart;
        let x = point.plotX + chart.plotLeft;
        let y = point.plotY + chart.plotTop - 100;

        if (x + labelWidth > chart.chartWidth) {
          x = chart.chartWidth - labelWidth - 10;
        }
        if (y < 0) {
          y = 50;
        }
        return { x: x, y: y };
      },
      formatter: function () {
        const date = new Date(this.x);
        const options = {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
          weekday: 'long',
        };

        const formattedDate = date
          .toLocaleString(intl.formatMessage({ id: 'app.locale' }), options)
          .replace(',', '');

        let s = `<b>${formattedDate}</b><br/>`;

        this.points.forEach((point) => {
          s += `<span style="color:${point.series.color}">\u25CF</span> ${
            point.series.name
          }: <b>${Highcharts.numberFormat(point.y, 2)}</b> %<br/>`;
        });

        return s;
      },
    },
    series: [
      {
        name: intl.formatMessage({
          id: 'app.deepview.demand.chart.demandSerie.label',
        }),
        turboThreshold: 0,
        data: dataChart,
        id: 'dataseries',

        tooltip: {
          valueDecimals: 4,
        },
      },
    ],
    legend: {
      itemStyle: {
        fontSize: '16px',
        fontWeight: 'bold',
      },
    },
  });

  const createSeries = (lineKey) =>
    selectData.map((harmonicIndex, idx) => ({
      name: `H${harmonicIndex + 2}`,
      data: dataChart.map((point) => [
        point.date.getTime(),
        point[lineKey][idx],
      ]),
      color: colors[idx],
      marker: { enabled: false },
      type: 'line',
    }));

  const createOptions = (lineKey, label) => ({
    chart: { height: 400, zoomType: 'x' },
    xAxis: {
      type: 'datetime',
      title: { text: 'Fecha y Hora' },
    },
    yAxis: {
      title: { text: label },
    },
    tooltip: {
      xDateFormat: '%d/%m/%Y %H:%M',
      shared: true,
    },
    legend: { enabled: true },
    series: createSeries(lineKey, label),
  });

  const handleDialog = () => setIsOpen(!isOpen);
  const dialogProps = fullScreen
    ? { fullScreen: true }
    : { fullWidth: true, maxWidth: 'xl' };

  const dialogTitle = (title) => {
    switch (title) {
      case TITLE.V_HAROMINC:
        return TITLES.V_HAROMINC;
      case TITLE.I_HARMONIC:
        return TITLES.I_HARMONIC;
      case TITLE.V_INTERHARMONIC:
        return TITLES.V_INTERHARMONIC;
      case TITLE.I_INTERHARMONIC:
        return TITLES.I_INTERHARMONIC;
      default:
        return 'Harmonic Chart';
    }
  };

  return (
    <Box
      bgcolor={color}
      width="100%"
      p={4}
      borderRadius={2}
      sx={{ cursor: 'pointer' }}
      onClick={handleDialog}
      height="100%"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        height="100%"
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap="4"
          height="100%"
        >
          <Typography
            sx={{ fontWeight: '700', fontSize: '1rem', color: '#fff' }}
            variant="h6"
          >
            {dialogTitle(title)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff' }}>
            {footer}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          bgcolor="white"
          borderRadius="100%"
        >
          {icon}
        </Box>
      </Box>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onClick={(e) => e.stopPropagation()}
        {...dialogProps}
        slots={{ transition: Transition }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Chip
              label={dialogTitle(title)}
              sx={{
                fontWeight: 700,
                backgroundColor: '#730065',
                fontSize: '16px',
                color: 'white',
              }}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip
                title={
                  fullScreen
                    ? 'Salir de Pantalla Completa'
                    : 'Pantalla Completa'
                }
                placement="left"
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setFullScreen(!fullScreen)}
                  endIcon={fullScreen ? <FullscreenExit /> : <Fullscreen />}
                >
                  {fullScreen
                    ? 'Salir de Pantalla Completa'
                    : 'Pantalla Completa'}
                </Button>
              </Tooltip>
              <Box display="flex" gap={1}>
                <Download name={dialogTitle(title)} itemRef={chartRef} />
                <ShareButton itemRef={chartRef} name={dialogTitle(title)} />
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormGroup row sx={{ mb: 2 }}>
            {[...Array(39)].map((_, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectData.includes(index)}
                    color="secondary"
                    onChange={(e) => {
                      const updated = e.target.checked
                        ? [...selectData, index]
                        : selectData.filter((i) => i !== index);
                      setSelectData(updated);
                    }}
                  />
                }
                label={`H${index + 2}`}
              />
            ))}
          </FormGroup>

          <Box width="100%" ref={chartRef}>
            {/* Main Chart */}
            <Box mb={4}>
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                {intl.formatMessage({ id: 'app.deepview.current' })} 1
              </Typography>
              <HighchartsReact
                highcharts={Highcharts}
                options={createOptions(
                  'l1',
                  intl.formatMessage({ id: 'app.deepview.current' }) + ' 1'
                )}
              />
            </Box>
            {/* Secondary Chart */}
            <Box mb={4}>
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                {intl.formatMessage({ id: 'app.deepview.current' })} 2
              </Typography>
              <HighchartsReact
                highcharts={Highcharts}
                options={createOptions(
                  'l2',
                  intl.formatMessage({ id: 'app.deepview.current' }) + ' 2'
                )}
              />
            </Box>
            {/* Tertiary Chart */}
            <Box mb={4}>
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                {intl.formatMessage({ id: 'app.deepview.current' })} 3
              </Typography>
              <HighchartsReact
                highcharts={Highcharts}
                options={createOptions(
                  'l3',
                  intl.formatMessage({ id: 'app.deepview.current' }) + ' 3'
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={handleDialog} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
